/* @flow */
import { createSelector } from 'reselect';
import queryString from 'query-string';

import StringUtils from 'common/utils/StringUtils';
import TimeUtils from 'common/utils/TimeUtils';
import { DateRangeEnum } from 'common/utils/DateRangeUtils';
import {
    personnelActiveBUTeamsSelector,
    personnelActiveTeamsSelector,
    personnelBUTeamsSelector
} from 'common/redux/selectors/personnelTeamsSelector';
import { LiveViewTabs } from 'common/enums/LiveViewEnums';
import type { Location, WithRouterProps } from 'common/components/core/router';
import { getShouldIncludeInactiveTeams } from 'common/redux/selectors/appPreferences';
import type { AppState } from 'common/redux/reducers';

export const clientSelector = function (state, props): ?string {
    return props.match.params && props.match.params.clientId;
};
export const clientSelectorV2 = (params: any): ?string => {
    return params?.clientId;
};
export const minTaskCountSelector = (state, props) =>
    props.location.query.minTaskCount;
export const maxTaskCountSelector = (state, props) =>
    props.location.query.maxTaskCount;
export const userIdSelector = (state, props) =>
    props.match.params.userId || props.location.query.entityId;
export const batchIdSelector = (state, props): ?string =>
    props.match.params.batchId;
export const homebaseIdQuerySelector = (state, props) =>
    props.location.query.homebaseId;
export const homebaseOptionIdsQuerySelector = (state, props) =>
    props.location.query.homebaseOptionIds;
export const planIdSelector = (state, props): ?string => {
    const currentQuery = queryString.parse(props.location.search);
    return props.match.params.planId || currentQuery.planId;
};
export const crossdockQuerySelector = (state, props) => {
    return props.location.query.isCrossDock === 'true'; // convert from string to boolean
};
export const selectedDashboardIdSelector = (_, props): ?string => {
    return props.match.params.dashboardId;
};
export const typeSelector = (state, props) => props.match.params.type;
export const graphIdSelector = (state, props) => props.match.params.graphId;
export const dashboardIdSelector = (_, props) =>
    props.match.params.dashboardId || 'overview';
export const taskStatusSelector = (state, props) =>
    props.location.query.taskStatus || 'ONGOING';

/**
    @DEPRECATED
    Reason - Not every component will also have props as location.
        We have to be specific in some selectors for the props they need. And there by `selectedDateSelectorV2` exists
*/
export const selectedDateSelector = (state, props) => {
    const query = new URLSearchParams(props.location.search);
    return query.get('date') || TimeUtils.formatWithEnglishLocale();
};
export const selectedDateSelectorV2 = location => {
    return selectedDateSelector(null, { location });
};
export const activeTeamIdSelector = (state, props) =>
    props.location.query.teamIds?.split(',')[0] ||
    activePersonnelTeamIdsSelector(state, props)?.[0];
export const selectedDateRangeSelector = (state, props) => {
    const { range, startDate, endDate } = props.location.query;
    if (range) {
        switch (range) {
            case DateRangeEnum.last7Days:
            case DateRangeEnum.last30Days:
            case DateRangeEnum.last182days:
            case DateRangeEnum.last365days:
                return range;
        }
    } else if (
        typeof range === 'undefined' &&
        typeof startDate === 'undefined' &&
        typeof endDate === 'undefined'
    ) {
        const selectedRange = 'today';
        return selectedRange;
    }
    return null;
};
export const selectedMultiDateSelector = (state, props) => {
    let { date } = props.location.query;
    const { startDate, endDate, range } = props.location.query;

    if (range) {
        const newDate = {};
        switch (range) {
            case DateRangeEnum.last7Days:
                newDate.startDate = TimeUtils.getMomentWithEnglishLocale()
                    .subtract(6, 'days')
                    .format('YYYY-MM-DD');
                newDate.endDate = TimeUtils.formatWithEnglishLocale();
                return newDate;
            case DateRangeEnum.last30Days:
                newDate.startDate = TimeUtils.getMomentWithEnglishLocale()
                    .subtract(29, 'days')
                    .format('YYYY-MM-DD');
                newDate.endDate = TimeUtils.formatWithEnglishLocale();
                return newDate;
            case DateRangeEnum.last182days:
                newDate.startDate = TimeUtils.getMomentWithEnglishLocale()
                    .subtract(181, 'days')
                    .format('YYYY-MM-DD');
                newDate.endDate = TimeUtils.formatWithEnglishLocale();
                return newDate;
            case DateRangeEnum.last365days:
                newDate.startDate = TimeUtils.getMomentWithEnglishLocale()
                    .subtract(365, 'days')
                    .format('YYYY-MM-DD');
                newDate.endDate = TimeUtils.formatWithEnglishLocale();
                return newDate;
        }
    } else if (startDate && endDate) {
        return { startDate, endDate };
    } else if (startDate || endDate) {
        return {
            startDate: startDate ? startDate : endDate,
            endDate: endDate ? endDate : ''
        };
    }
    const CURRENT_DATE = TimeUtils.formatWithEnglishLocale();
    if (!date) {
        date = CURRENT_DATE;
    }
    return {
        startDate: date,
        endDate: ''
    };
};

export const shiftIdSelector = (state, props) =>
    props.location.query.shiftId || 'allday';
export const getCurrentPathName = props => {
    if (props && props.routes) {
        const currentPath = props.routes[props.routes.length - 1];
        return currentPath.path;
    }
};
export const isSelectedDatePresentAndToday = (state, props) => {
    if (props.location && props.location.query) {
        const selectedDate = selectedDateSelector(state, props);
        const pathName = getCurrentPathName(props);
        if (StringUtils.isEmpty(selectedDate)) {
            return false;
        } else {
            if (pathName === 'live_view') {
                const today = TimeUtils.now();
                const isToday = TimeUtils.isSame(
                    props.location.query.date,
                    today,
                    'day'
                );
                return isToday;
            }
            return false;
        }
    }
    return false;
};

export const teamIdsSelector = createSelector(
    [
        (state, props) => {
            const query = new URLSearchParams(props.location.search);
            return query.get('teamIds');
        }
    ],
    teamIds => {
        if (!teamIds) {
            return [];
        }
        var teamIdsList = teamIds ? teamIds.split(',') : null;
        if (!teamIdsList) {
            return [];
        } else if (teamIdsList.indexOf('all_teams') !== -1) {
            return [];
        }
        return teamIdsList;
    }
);

export const getQueryStringWithSelectedTeams = (
    state: AppState,
    props: { location: Location }
): string => {
    const { location } = props;
    const selectedTeams = teamIdsSelector(state, { location });
    const updatedQueryParams = new URLSearchParams(location.search);
    updatedQueryParams.delete('teamIds');
    if (selectedTeams.length > 0) {
        updatedQueryParams.set('teamIds', selectedTeams.join(','));
    }
    return `?${updatedQueryParams.toString()}`;
};

const dateQuerySelector = function (state, props) {
    return props.location.query.month || props.location.query.week;
};

export const viewFromDateSelector = createSelector(
    [dateQuerySelector],
    selectedDate => {
        const dateFormat = 'DDMMMYYYY';
        const monthFormat = 'MMMYYYY';
        const selectedDateValue = selectedDate || null;
        if (!selectedDateValue) {
            return TimeUtils.getMomentWithEnglishLocale();
        }
        if (
            TimeUtils.getMomentWithEnglishLocale(
                selectedDateValue,
                dateFormat,
                true
            ).isValid() ||
            TimeUtils.getMomentWithEnglishLocale(
                selectedDateValue,
                monthFormat,
                true
            ).isValid()
        ) {
            return TimeUtils.getMomentWithEnglishLocale(
                selectedDateValue,
                'DDMMMYYYY'
            );
        }
        return TimeUtils.getMomentWithEnglishLocale();
    }
);

export const selectedTeamIdsSelector = createSelector(
    [
        personnelBUTeamsSelector,
        teamIdsSelector,
        getShouldIncludeInactiveTeams,
        personnelActiveBUTeamsSelector
    ],
    (allTeams, teamIds, shouldIncludeInactiveTeams, activeTeams) => {
        if (teamIds.length === 0) {
            const teams = shouldIncludeInactiveTeams ? allTeams : activeTeams;
            return teams.map(team => team.get('teamId')).toArray();
        }
        return teamIds;
    }
);

export const taskIdSelector = (state, props) =>
    props.location.query.selectedTaskId;
export const selectedTourIdSelector = (state, props) =>
    props.location.query.tourId;
export const selectedGraphIdSelector = (_, props) =>
    props.location.query.selectedGraphId;
export const batchDateSelector = (state, props) => props.location.query.date;
export const teamIdQuerySelector = (_, props) => props.location.query.teamId;

/* Entity Management Speciic Selectors */
export const isAddEntityActive = (state, props) =>
    props.location.query.add === null;
export const editEntityIdSelector = (state, props) => props.location.query.edit;
export const currentMapIdSelector = (state, props) =>
    props.match.params.mapId || props.mapId;
export const mapActiveStatusSelector = (state, props) => {
    const { versionStatus } = props.match.params;
    return versionStatus && versionStatus.toLowerCase() === 'active';
};
export const mapVersionSelector = (state, props) => {
    const { versionStatus, versionNumber } = props.match.params;
    if (versionStatus && versionStatus.toLowerCase() === 'version') {
        return versionNumber;
    }
};

export const activeTeamIdsSelector = createSelector(
    [teamIdsSelector, personnelActiveBUTeamsSelector],
    (selectedTeamIds, personnelActiveBUTeams) => {
        const teamIds = [...selectedTeamIds];
        if (selectedTeamIds.length === 0) {
            personnelActiveBUTeams.forEach(team => teamIds.push(team.teamId));
        }
        return teamIds;
    }
);

export const activePersonnelTeamIdsSelector = createSelector(
    [personnelActiveTeamsSelector],
    allPersonnelTeams => {
        return allPersonnelTeams.map(team => team.teamId).toJS();
    }
);

export const isImportEntityActive = (state, props) =>
    props.location.query.import === null;

export const isExportEntityActive = (state, props) =>
    props.location.query.entityExport === null;

export const isPlanExportActive = (state, props) =>
    props.location.query.planExport === null;

export const isChangePasswordActive = (state, props) =>
    props.location.query.changePassword === null;

/* --- Live View v2 selectors --- */
export const selectedStatusSelector = (state, props) =>
    props.location.query.selectedStatus;

export const selectedViewSelector = (state, props) => props.location.query.view;

export const currentLiveViewTabSelector = (state, props) =>
    LiveViewTabs.values().find(tab => props.location.pathname.search(tab) > -1);

/* --- Single trip screen selectors --- */

export const queryTripIdSelector = (state, props) => {
    return props.location.query.tripId;
};

export const selectedEntityIdSelector = (state, props) =>
    props.location.query.entityId;

/* ------- Network Optimization Selectors --------*/

export const isImportStatusActive = (state, props) =>
    props.location.query.importStatus === null;

export const selectedImportStatusSelector = (state, props) =>
    props.location.query.selectedImportId;

export const importIdPollSelector = (state, props) => props.location.query.poll;

export const selectedStudyId = (state, props) => props.location.query.studyId;

export const addProjectSelector = (state: {}, props: WithRouterProps) =>
    props.location.query.addProject === null;
export const selectedProjectIdSelector = (state: {}, props: WithRouterProps) =>
    props.match.params && props.match.params.projectId;
export const confirmProceedSelector = (state, props) =>
    props.location.query.confirm;
export const isShowSelectedEntitiesActive = (state, props) =>
    props.location.query.showSelectedEntities === null;

export const addDemandLayerSelector = (state: {}, props: WithRouterProps) =>
    props.location.query.addDemandLayer === null;

export const selectedStudyIdSelector = (state: {}, props: WithRouterProps) =>
    props.match.params.studyId || '';

export const isDeleteProjectActive = (
    state: {},
    props: WithRouterProps
): boolean => {
    return props.location.query.deleteProject === null;
};
